import React, { useState, useEffect, useRef } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaColumns,
  FaFilter,
} from "react-icons/fa";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import ModifyColumns from "./../Tools/ModifyColumns";
import axios from "axios";
import { format } from "date-fns";
import {
  AddTableFilterComponent,
  apiRequestBodyBuilder,
  getSortedDataGLOBAL,
} from "../config/tableHelper";
import { RenderTable } from "../config/tableWrapper";
import { BIDSTRAT_ADDTABLE_COLS } from "../Components/AddTableCols";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// bid strat cols

const OnboardingBid = ({ initialDateRange, PageType }) => {
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [columns, setColumns] = useState(BIDSTRAT_ADDTABLE_COLS);
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [tableVisible, setTableVisible] = useState(true);

  const [groups, setGroups] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [campaignNameInput, setCampaignNameInput] = useState("");

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);

  const columnRef = useRef(null);
  const filterRef = useRef(null);

  const fetchData = async (nameContains = "") => {
    setLoading(true);
    setUnfilteredData([]);

    let requestBody = apiRequestBodyBuilder(initialDateRange, {
      reporting_type: PageType,
    });

    if (nameContains) {
      requestBody.name_contains = nameContains;
    }

    const endpoint = nameContains
      ? "https://api.confidanto.com/bidstrategies/filter"
      : "https://api.confidanto.com/campaigns/bid-strategy-used";

    try {
      const response = await axios.post(endpoint, requestBody);
      const responseData = response.data;

      if (responseData && responseData.length > 0) {
        const currency = responseData[0].currency_code;
        setCurrencySymbol(currency === "USD" ? "$" : "₹");
        setUnfilteredData(responseData);
      } else {
        setUnfilteredData([]);
      }
      setColumns(BIDSTRAT_ADDTABLE_COLS);
    } catch (error) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const [currentGroupType, setCurrencyGroupType] = useState("");

  const fetchGroups = async () => {
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange);

      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (!responseInfo.ok) {
        throw new Error(
          `Failed to fetch group info. Status: ${responseInfo.status}`,
        );
      }
      const groupType = await responseInfo.json();
      console.info("GENDER RES Info", groupType, groupType.type);
      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      const TYPEQUERY =
        typeForQuery?.charAt(0).toUpperCase() + typeForQuery?.slice(1);
      setCurrencyGroupType(TYPEQUERY);

      requestBody.type = typeForQuery;

      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      setGroups([]);
    }
  };

  const handleApplyCampaignFilter = () => {
    fetchData(campaignNameInput);
    setShowFilterDropdown(false);
    setSelectedFilter(null);

    const newFilter = {
      id: Date.now(),
      type: "campaign",
      value: campaignNameInput,
      label: `Campaign: ${campaignNameInput}`,
    };
    setAppliedFilters([newFilter]);
  };

  const handleGroupApply = async (remainingGroups = null) => {
    setLoading(true);
    setUnfilteredData([]);
    setShowFilterDropdown(false);
    setSelectedFilter(null);

    const customer_id = localStorage.getItem("customer_id");
    const email = localStorage.getItem("email") || "default@example.com";
    const activeGroups =
      remainingGroups !== null ? remainingGroups : selectedGroups;
    const selectedGroupIds = groups
      .filter((g) => activeGroups.includes(g.group_name))
      .map((g) => g.group_id);

    if (activeGroups.length === 0) {
      alert("Please select at least one group.");
      setLoading(false);
      return;
    }

    const groupFilters = activeGroups.map((group) => ({
      id: group,
      type: "groupwise",
      value: group,
      label: group,
    }));
    setAppliedFilters(groupFilters);

    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange, {
        group_ids: selectedGroupIds,
      });

      const response = await axios.post(
        "https://api.confidanto.com/bid-strategy-group/datewise-data",
        requestBody,
      );

      const responseData = response.data;
      if (responseData && responseData.length > 0) {
        const currency = responseData[0]?.group_totals?.currency;
        setCurrencySymbol(currency === "USD" ? "$" : "₹");
      }

      const formatted = responseData.map((item) => ({ ...item }));

      setUnfilteredData(formatted);
      setColumns((prevColumns) =>
        prevColumns.map((col) =>
          col.key === "bid_strategy_type"
            ? { ...col, title: "Group", key: "group_name" }
            : col,
        ),
      );
    } catch (error) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyStaticFilter = (filter) => {
    setAppliedFilters((prev) => [...prev, filter]);
    setShowFilterDropdown(false);
  };

  const handleRemoveFilter = async (filterToRemove) => {
    const newFilters = appliedFilters.filter((f) => f.id !== filterToRemove.id);

    if (filterToRemove.type === "groupwise") {
      const remainingGroups = newFilters
        .filter((f) => f.type === "groupwise")
        .map((f) => f.value);
      setSelectedGroups(remainingGroups);

      if (remainingGroups.length === 0) {
        setAppliedFilters([]);
        fetchData();
      } else {
        handleGroupApply(remainingGroups);
      }
    } else {
      setAppliedFilters(newFilters);
      if (filterToRemove.type === "campaign" && newFilters.length === 0) {
        fetchData();
      }
    }
  };

  const getFilteredData = () => {
    const staticFilters = appliedFilters.filter((f) => f.type === "static");
    if (staticFilters.length === 0) {
      return unfilteredData;
    }

    return unfilteredData.filter((row) => {
      return staticFilters.every((filter) => {
        const rowValue =
          typeof row[filter.key] === "object"
            ? row[filter.key].value
            : row[filter.key];
        const filterValue = filter.value;

        if (rowValue === null || rowValue === undefined) return false;

        if (filter.keyType === "number") {
          const rowNum = parseFloat(rowValue);
          const filterNum = parseFloat(filter.value);
          if (isNaN(rowNum) || isNaN(filterNum)) return false;
          switch (filter.comparison) {
            case ">":
              return rowNum > filterNum;
            case "<":
              return rowNum < filterNum;
            case "=":
              return rowNum === filterNum;
            default:
              return true;
          }
        } else if (filter.keyType === "boolean") {
          return filter.value.includes(String(rowValue));
        } else {
          return rowValue
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        }
      });
    });
  };

  const filteredData = getFilteredData();

  useEffect(() => {
    fetchGroups();
    fetchData();
    setAppliedFilters([]);

    const customer_id = localStorage.getItem("customer_id");
    if (customer_id === "7948821642" || customer_id === "1176825945") {
      setCurrencySymbol("$");
    } else {
      setCurrencySymbol("₹");
    }
  }, [
    initialDateRange[0].startDate,
    initialDateRange[0].endDate,
    initialDateRange.length,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="flex h-full bg-gray-100 font-roboto mt-3 text-gray-600">
      <main className="w-full p-4">
        <div className="flex justify-between items-center mb-3">
          <div
            className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
          >
            <AddTableFilterComponent
              data={unfilteredData}
              filterRef={filterRef}
              showFilterDropdown={showFilterDropdown}
              setShowFilterDropdown={setShowFilterDropdown}
              appliedFilters={appliedFilters}
              handleRemoveFilter={handleRemoveFilter}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              onFetchGroups={fetchGroups}
              columns={columns}
              campaignNameInput={campaignNameInput}
              setCampaignNameInput={setCampaignNameInput}
              onApplyCampaignFilter={handleApplyCampaignFilter}
              groups={groups}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              onApplyGroupFilter={handleGroupApply}
              onApplyStaticFilter={handleApplyStaticFilter}
              currentGroupType={currentGroupType}
            />
          </div>
          <div className="flex space-x-2">
            <div className="relative" ref={columnRef}>
              <button
                disabled={loading}
                className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center justify-center transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-100"
                }`}
                onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              >
                <FaColumns className="text-sm " />
                <span className="text-sm">Columns</span>
              </button>
              {showColumnsMenu && (
                <ModifyColumns
                  columns={columns}
                  setColumns={setColumns}
                  setTableVisible={setTableVisible}
                  setShowColumnsMenu={setShowColumnsMenu}
                  colsLimit={13}
                />
              )}
            </div>
          </div>
        </div>

        {/* Table & Skeleton Loading */}
        {loading ? (
          <TableSkeleton />
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow">
            {filteredData.length > 0 ? (
              <RenderTable
                columns={columns}
                data={filteredData}
                enableExpandCollapse={initialDateRange.length > 1}
              />
            ) : (
              <div className="flex justify-center items-center h-40 mt-6">
                <h2 className="text-lg">No Data Found</h2>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default OnboardingBid;
